'use client';

import { useCallback, useMemo } from 'react';
import { observer } from '@legendapp/state/react';
import { GiftItem } from '../gift-item/gift-item';
import type { GiftDataProps } from './gift-data.types';
import type { GiftProductItem } from '../../../types/gift-selection.types';
import styles from './gift-data.module.css';
import { Text } from '../../../../../utils';
import { AxisEnum, ScrollView } from '@kidslabs/mario-nextjs-react-ui-core';
import { GiftLayoutEnum } from '../../../../../enums';
export const GiftData = observer(
    ({
        giftData,
        title,
        direction = 'horizontal',
        containerClassName,
        itemDisabled$,
        highlighted,
        // NEW: Props for standalone usage
        selectedGifts$ = undefined, // Observable
        productQuantity$ = undefined, // Observable
        selectionRound$ = undefined, // Observable
        onSelectGift = () => {},
        onRemove,
        selectedItem,
        sessionId,
        layout,
        enableAutoFill,
        onAutoFillRequired,
    }: GiftDataProps) => {
        // ✅ Get observable value for itemDisabled - observer() will re-render on change
        const isDisabled = itemDisabled$?.get() ?? false;

        // Build container classes
        const containerClasses = useMemo(() => {
            const classes = [
                styles.container,
                direction === 'horizontal' ? styles.horizontalContainer : styles.verticalContainer,
                isDisabled && !enableAutoFill && styles.disabled,
                highlighted && styles.highlighted,
                containerClassName,
            ].filter(Boolean);
            return classes.join(' ');
        }, [direction, isDisabled, highlighted, containerClassName, enableAutoFill]);

        // Show selected gifts or available gifts based on highlighted state
        const displayData: GiftProductItem[] = useMemo(() => {
            if (highlighted && selectedItem) {
                return [selectedItem];
            }
            // Pass product objects through directly — no need to remap fields
            return (giftData.products?.filter((p): p is NonNullable<typeof p> => p !== null) ?? []).map(p => ({
                ...p,
                image: p.small_image ? { url: p.small_image.url } : null,
            })) as unknown as GiftProductItem[];
        }, [highlighted, selectedItem, giftData.products]);

        // Memoize keyExtractor
        const getItemKey = useCallback((item: GiftProductItem) => `gift-item-${item.id}-${item.sku}`, []);

        // Content classes based on direction
        const isSingleItem = displayData.length === 1;
        const isDoubleItem = displayData.length > 2;

        // Wrapper (list container)
        const itemWrapperClass = useMemo(() => {
            if (direction === 'horizontal') {
                return isSingleItem ? styles.horizontalWrapperFull : styles.horizontalWrapperSingle;
            }

            return styles.verticalItem;
        }, [direction, isSingleItem]);

        // Item
        const itemContentClass = useMemo(() => {
            if (direction !== 'horizontal') {
                return styles.verticalItem;
            }

            if (layout === GiftLayoutEnum.SINGLE) {
                return isSingleItem ? styles.horizontalItemFull : styles.horizontalItemSingle;
            }

            if (isSingleItem) {
                return styles.horizontalItemFull;
            }

            if (isDoubleItem) {
                return styles.horizontalItemSingle;
            }

            return styles.horizontalItemDouble;
        }, [direction, layout, isSingleItem, isDoubleItem]);

        const isShowScrollView = useMemo(() => {
            return layout === GiftLayoutEnum.SINGLE ? !isSingleItem : isDoubleItem;
        }, [layout, isSingleItem, isDoubleItem]);

        return (
            <div className={containerClasses} data-testid="gift-option-radio">
                <div className={styles.top}>
                    <svg className={styles.heartIcon} width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                    </svg>
                    <div className={styles.viewTitle}>
                        <Text size="medium" weight="medium" className={styles.title}>
                            {title}
                        </Text>
                    </div>
                </div>
                <div className={styles.horizontalGroup}>
                    <ScrollView
                        isVisible={isShowScrollView}
                        classNames={{
                            trackX: styles.positionThumb,
                        }}
                        axis={AxisEnum.X}
                        trackLengthPercent={0.2}
                    >
                        <div className={itemWrapperClass}>
                            {displayData.map(item => (
                                <div key={getItemKey(item)} className={itemContentClass}>
                                    <GiftItem
                                        giftItem={item}
                                        itemDisabled$={itemDisabled$}
                                        giftData={giftData}
                                        highlighted={highlighted}
                                        selectedGifts$={selectedGifts$}
                                        productQuantity$={productQuantity$}
                                        selectionRound$={selectionRound$}
                                        onSelectGift={onSelectGift}
                                        onRemove={onRemove}
                                        sessionId={sessionId}
                                        enableAutoFill={enableAutoFill}
                                        onAutoFillRequired={onAutoFillRequired}
                                    />
                                </div>
                            ))}
                        </div>
                    </ScrollView>
                </div>
            </div>
        );
    }
);
